import { Message } from '@arco-design/web-react';
import { camelizeKeys } from 'humps';
import Http from '@/http';
import { HttpCode } from '@/http/config';
import { LocalStorageKey } from '@/constants/storage';
import { logout } from '@/utils/auth';
import { getOrg } from '@/utils/hooks/useOrg';
import {
  ASSISTANT_CHAT_BUSY_REASON,
  AssistantChatSource,
  AssistantMessageRole,
} from '@/constants/business/assistant';
import {
  IAssistantConfig,
  IAssistantConversation,
  IAssistantConversationDetail,
  IAssistantConversationList,
  IAssistantKbUploadItem,
  IAssistantKbUploadResult,
  IAssistantKnowledge,
  IAssistantMessage,
  IAssistantProcessStep,
  IAssistantStats,
} from '@/api/typings/assistant';

interface IConversationNoParams {
  conversationNo: string;
}

interface ISearchAssistantConversationsParams {
  keyword?: string;
  source?: AssistantChatSource;
  page?: number;
  size?: number;
}

interface IExportAssistantConversationsParams {
  keyword?: string;
  source?: AssistantChatSource;
}

export interface IAssistantStreamResult {
  source?: string;
  unanswered: boolean;
  error?: string;
  reason?: string;
}

interface IStreamAssistantChatParams {
  conversationId: string;
  content: string;
  onChunk: (text: string) => void;
  onProcessSteps?: (steps: IAssistantProcessStep[]) => void;
  signal?: AbortSignal;
}

interface IAssistantMessageItem {
  role?: string;
  content?: string;
  unansweredType?: string;
  ctime?: string;
  tool?: string;
  sourcesJson?: unknown;
}

interface IAssistantMessageSources {
  input?: unknown;
  output?: string;
  tool?: string;
}

const StreamEventType = {
  TEXT: 'text',
  THINKING: 'thinking',
  TOOL_USE: 'tool_use',
  TOOL_RESULT: 'tool_result',
  STATUS: 'status',
  ERROR: 'error',
  FINAL: 'final',
} as const;

interface IAssistantSsePayload {
  type?: string;
  content?: string;
  tool?: string;
  name?: string;
  input?: unknown;
  output?: string;
  summary?: string;
  source?: string;
  unanswered?: boolean;
  unansweredType?: string;
  final?: boolean;
  message?: string;
  reason?: string;
}

const parseSources = (value: unknown): IAssistantMessageSources => {
  let source: unknown = value;
  if (typeof value === 'string' && value) {
    try {
      source = JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (!source || typeof source !== 'object') {
    return {};
  }
  return {
    input: 'input' in source ? source.input : undefined,
    output:
      'output' in source && typeof source.output === 'string'
        ? source.output
        : undefined,
    tool:
      'tool' in source && typeof source.tool === 'string'
        ? source.tool
        : undefined,
  };
};

const readHistoryToolName = (
  item: IAssistantMessageItem,
  sources: IAssistantMessageSources
) => {
  if (sources.tool) {
    return sources.tool;
  }
  if (item.tool) {
    return item.tool;
  }
  const fromContent = (item.content || '').replace(/^调用\s*/, '').trim();
  return fromContent || '工具';
};

const appendThinking = (steps: IAssistantProcessStep[], text: string) => {
  if (!text) {
    return;
  }
  const last = steps[steps.length - 1];
  if (last && last.kind === 'thinking') {
    last.text += text;
    return;
  }
  steps.push({ kind: 'thinking', text });
};

const fillToolOutput = (
  steps: IAssistantProcessStep[],
  output: string,
  tool?: string
) => {
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    const step = steps[index];
    if (step.kind === 'tool' && !step.output && (!tool || step.tool === tool)) {
      steps[index] = { ...step, output };
      break;
    }
  }
};

const mapMessages = (
  items: IAssistantMessageItem[] = []
): IAssistantMessage[] => {
  const messages: IAssistantMessage[] = [];
  let processSteps: IAssistantProcessStep[] = [];

  const flushAssistant = (
    item: IAssistantMessageItem,
    index: number,
    content: string
  ) => {
    messages.push({
      id: `${item.ctime || 'msg'}-${index}`,
      role: AssistantMessageRole.ASSISTANT,
      content,
      processSteps: processSteps.length > 0 ? processSteps : undefined,
      unanswered: Boolean(item.unansweredType),
    });
    processSteps = [];
  };

  items.forEach((item, index) => {
    const role = item.role || '';
    if (role === AssistantMessageRole.USER) {
      processSteps = [];
      messages.push({
        id: `${item.ctime || 'msg'}-${index}`,
        role: AssistantMessageRole.USER,
        content: item.content || '',
        unanswered: Boolean(item.unansweredType),
      });
      return;
    }
    if (role === StreamEventType.THINKING) {
      appendThinking(processSteps, item.content || '');
      return;
    }
    if (role === StreamEventType.TOOL_USE) {
      const sources = parseSources(item.sourcesJson);
      processSteps.push({
        kind: 'tool',
        tool: readHistoryToolName(item, sources),
        input: sources.input,
      });
      return;
    }
    if (role === StreamEventType.TOOL_RESULT) {
      const sources = parseSources(item.sourcesJson);
      fillToolOutput(processSteps, sources.output || item.content || '');
      return;
    }
    if (role === StreamEventType.STATUS) {
      return;
    }
    if (item.content) {
      flushAssistant(item, index, item.content);
    }
  });

  return messages;
};

const getAuthHeaders = () => {
  let { orgId = '' } = getOrg();
  if (!orgId) {
    orgId = localStorage.getItem(LocalStorageKey.ORGANIZATION_ID) || '';
  }
  return {
    h_mb_token: localStorage.getItem(LocalStorageKey.TOKEN) || '',
    organization_id: orgId,
  };
};

const uploadKnowledgeFile = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('key_prefix', 'upload/assistant-kb');
  const response = await fetch('/hr/file_upload_agent', {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: formData,
  });
  if (!response.ok) {
    throw new Error('文件上传失败');
  }
  const result: { key?: string; data?: { key?: string }; message?: string } =
    await response.json();
  const key = result.key || result.data?.key;
  if (!key) {
    throw new Error(result.message || '文件上传失败');
  }
  return key;
};

const emitProcessSteps = (
  steps: IAssistantProcessStep[],
  onProcessSteps?: (next: IAssistantProcessStep[]) => void
) => {
  onProcessSteps?.(steps.map((step) => ({ ...step })));
};

const applySsePayload = (
  payload: IAssistantSsePayload,
  state: {
    content: string;
    processSteps: IAssistantProcessStep[];
    source?: string;
    unanswered: boolean;
    error?: string;
    reason?: string;
  },
  onChunk: (text: string) => void,
  onProcessSteps?: (steps: IAssistantProcessStep[]) => void
) => {
  switch (payload.type) {
    case StreamEventType.TEXT:
      if (payload.content) {
        state.content += payload.content;
        onChunk(state.content);
      }
      return false;
    case StreamEventType.THINKING:
      if (!payload.content) {
        return false;
      }
      appendThinking(state.processSteps, payload.content);
      emitProcessSteps(state.processSteps, onProcessSteps);
      return false;
    case StreamEventType.TOOL_USE:
      state.processSteps.push({
        kind: 'tool',
        tool: payload.tool || payload.name || '工具',
        input: payload.input,
      });
      emitProcessSteps(state.processSteps, onProcessSteps);
      return false;
    case StreamEventType.TOOL_RESULT:
      fillToolOutput(
        state.processSteps,
        payload.output || payload.content || payload.summary || '',
        payload.tool || payload.name
      );
      emitProcessSteps(state.processSteps, onProcessSteps);
      return false;
    case StreamEventType.STATUS:
      return false;
    case StreamEventType.ERROR: {
      const message = payload.content || payload.message || '对话失败';
      state.error = message;
      state.reason = payload.reason;
      if (payload.reason === ASSISTANT_CHAT_BUSY_REASON) {
        return true;
      }
      state.content = message;
      onChunk(state.content);
      return true;
    }
    case StreamEventType.FINAL:
      if (payload.source) {
        state.source = payload.source;
      }
      if (payload.unanswered !== undefined) {
        state.unanswered = payload.unanswered;
      } else if (payload.unansweredType) {
        state.unanswered = true;
      }
      return true;
    default:
      return false;
  }
};

export function apiGetAssistantConfig() {
  return Http.get<undefined, IAssistantConfig>('/hr/assistant/status');
}

export function apiSetAssistantEnabled(params: { enabled: boolean }) {
  return Http.post<{ enabled: boolean }, { enabled: boolean }>(
    '/hr/assistant/config/enable',
    params
  );
}

export function apiSearchAssistantKnowledge() {
  return Http.get<undefined, { list?: IAssistantKnowledge[] }>(
    '/hr/assistant/kb/list'
  );
}

export function apiUploadKnowledgeOss(file: File) {
  return uploadKnowledgeFile(file);
}

export function apiUploadAssistantKnowledge(files: IAssistantKbUploadItem[]) {
  return Http.post<
    { files: IAssistantKbUploadItem[] },
    { list?: IAssistantKbUploadResult[] }
  >('/hr/assistant/kb/upload', { files });
}

export function apiRelearnAssistantKnowledge(params: { id: number }) {
  return Http.post<{ id: number }, IAssistantKbUploadResult>(
    '/hr/assistant/kb/relearn',
    params
  );
}

export function apiDeleteAssistantKnowledge(params: { id: number }) {
  return Http.post<{ id: number }, void>('/hr/assistant/kb/delete', params);
}

export function apiGetAssistantStats() {
  return Http.get<undefined, IAssistantStats>(
    '/hr/assistant/conversation/stats'
  );
}

export function apiSearchAssistantConversations(
  params: ISearchAssistantConversationsParams
) {
  return Http.get<
    ISearchAssistantConversationsParams,
    IAssistantConversationList
  >('/hr/assistant/conversation/list', params);
}

export function apiGetAssistantConversationDetail(
  params: IConversationNoParams
) {
  return Http.get<
    IConversationNoParams,
    {
      conversation?: IAssistantConversation;
      messages?: IAssistantMessageItem[];
    }
  >('/hr/assistant/conversation/detail', params).then((data) => {
    const result: IAssistantConversationDetail = {
      conversation: data.conversation,
      messages: mapMessages(data.messages),
    };
    return result;
  });
}

export function apiDeleteAssistantConversation(params: IConversationNoParams) {
  return Http.post<IConversationNoParams, void>(
    '/hr/assistant/conversation/delete',
    params
  );
}

export function apiExportAssistantConversations(
  params: IExportAssistantConversationsParams
) {
  return Http.post<IExportAssistantConversationsParams, Blob>(
    '/hr/assistant/conversation/export',
    params,
    {
      responseType: 'blob',
    }
  );
}

export function apiListMyAssistantChats(params?: {
  page?: number;
  size?: number;
}) {
  return Http.get<{ page?: number; size?: number }, IAssistantConversationList>(
    '/hr/assistant/my/conversations',
    params
  );
}

export function apiCreateAssistantChat() {
  return Http.post<Record<string, never>, { conversationNo?: string }>(
    '/hr/assistant/my/conversation/create',
    {}
  );
}

export function apiGetMyAssistantChatDetail(params: IConversationNoParams) {
  return Http.get<IConversationNoParams, { list?: IAssistantMessageItem[] }>(
    '/hr/assistant/my/conversation/messages',
    params
  ).then((data) => ({
    messages: mapMessages(data.list),
  }));
}

export function apiDeleteMyAssistantChat(params: IConversationNoParams) {
  return Http.post<IConversationNoParams, void>(
    '/hr/assistant/my/conversation/delete',
    params
  );
}

export async function apiStreamAssistantChat(
  params: IStreamAssistantChatParams
): Promise<IAssistantStreamResult> {
  const state: {
    content: string;
    processSteps: IAssistantProcessStep[];
    source?: string;
    unanswered: boolean;
    error?: string;
    reason?: string;
  } = {
    content: '',
    processSteps: [],
    unanswered: false,
  };
  try {
    const response = await fetch('/hr/assistant/chat/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        ...getAuthHeaders(),
      },
      credentials: 'include',
      signal: params.signal,
      body: JSON.stringify({
        conversation_no: params.conversationId,
        content: params.content,
      }),
    });
    if (response.status === HttpCode.UNAUTHORIZED) {
      logout();
      throw new Error('当前未登录，请先登录');
    }
    if (!response.ok) {
      throw new Error('对话请求失败');
    }
    const contentType = response.headers.get('content-type') || '';
    if (
      !contentType.includes('event-stream') &&
      contentType.includes('application/json')
    ) {
      const result: { ret?: number; detail?: string; msg?: string } =
        await response.json();
      if (result.ret && result.ret !== 1) {
        throw new Error(result.detail || result.msg || '对话请求失败');
      }
      return { source: state.source, unanswered: state.unanswered };
    }
    if (!response.body) {
      return { source: state.source, unanswered: state.unanswered };
    }
    const reader = response.body.getReader();
    const onAbort = () => {
      reader.cancel().catch(() => undefined);
    };
    params.signal?.addEventListener('abort', onAbort);
    if (params.signal?.aborted) {
      onAbort();
    }
    const decoder = new TextDecoder();
    let buffer = '';
    let finished = false;
    try {
      while (!finished) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const rawLine of lines) {
          const line = rawLine.replace(/\r$/, '');
          if (!line.startsWith('data:')) {
            continue;
          }
          const payload = line.slice(5).trim();
          if (!payload || payload === '[DONE]') {
            finished = payload === '[DONE]';
            continue;
          }
          let parsed: IAssistantSsePayload;
          try {
            parsed = camelizeKeys(JSON.parse(payload)) as IAssistantSsePayload;
          } catch {
            state.content += payload;
            params.onChunk(state.content);
            continue;
          }
          if (
            applySsePayload(
              parsed,
              state,
              params.onChunk,
              params.onProcessSteps
            )
          ) {
            finished = true;
          }
        }
      }
    } finally {
      params.signal?.removeEventListener('abort', onAbort);
    }
    if (params.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    return {
      source: state.source,
      unanswered: state.unanswered,
      error: state.error,
      reason: state.reason,
    };
  } catch (error) {
    if (params.signal?.aborted) {
      throw error;
    }
    const message = error instanceof Error ? error.message : '对话请求失败';
    Message.error(message);
    throw error;
  }
}
